import { z } from 'zod';

// ─── Auth ──────────────────────────────────────────────────────────────────

export const loginSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
  shopid: z.string().optional(),
  account: z.string().optional(),
  type: z.enum(['retail', 'manager']),
});

export type LoginInput = z.infer<typeof loginSchema>;

// ─── Customer ──────────────────────────────────────────────────────────────

export const customerSchema = z.object({
  id: z.string().optional(),
  account_id: z.string().optional(),
  shop_id: z.string().optional(),
  loyalty_points: z.number().optional(),
  credits: z.number().optional(),
  credit_pin: z.string().optional(),
  discount: z.number().min(0).max(100).optional(),
  metadata: z.string().optional(),
});

export type CustomerInput = z.infer<typeof customerSchema>;

export const customerRemarkSchema = z.object({
  customer_id: z.string().min(1),
  remark: z.string().min(1, 'Remark is required'),
  user_id: z.string().optional(),
});

export type CustomerRemarkInput = z.infer<typeof customerRemarkSchema>;

// ─── Inventory ─────────────────────────────────────────────────────────────

export const inventorySchema = z.object({
  id: z.string().optional(),
  account_id: z.string().optional(),
  shop_id: z.string().optional(),
  category: z.string().optional(),
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  qty: z.number().optional(),
  price: z.number().min(0).optional(),
  cost: z.number().min(0).optional(),
  pprice: z.number().min(0).optional(),
  status: z.string().optional(),
  barcode: z.string().optional(),
  supplier: z.string().optional(),
  metadata: z.string().optional(),
});

export type InventoryInput = z.infer<typeof inventorySchema>;

export const inventoryRecordSchema = z.object({
  inventory_id: z.string().min(1),
  incremental_qty: z.number(),
  action: z.string().min(1),
  remain: z.number().optional(),
  user_id: z.string().optional(),
  metadata: z.string().optional(),
});

export type InventoryRecordInput = z.infer<typeof inventoryRecordSchema>;

export const inventoryTransitSchema = z.object({
  inventory_id: z.string().min(1),
  source_shop_id: z.string().min(1),
  dest_shop_id: z.string().min(1),
  units: z.number().positive('Units must be positive'),
  user_id: z.string().optional(),
  metadata: z.string().optional(),
});

export type InventoryTransitInput = z.infer<typeof inventoryTransitSchema>;

// ─── Invoice ───────────────────────────────────────────────────────────────

export const invoiceLineItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  qty: z.number().positive(),
  price: z.number().min(0),
  cost: z.number().min(0).optional(),
  discount: z.number().min(0).optional(),
  discount_type: z.enum(['percentage', 'fixed']).optional(),
  category: z.string().optional(),
  barcode: z.string().optional(),
  item_class: z.string().optional(),
  metadata: z.record(z.unknown()).optional(),
});

export type InvoiceLineItemInput = z.infer<typeof invoiceLineItemSchema>;

export const invoiceDiscountSchema = z.object({
  type: z.enum(['percentage', 'fixed']),
  val: z.number().min(0),
});

export type InvoiceDiscountInput = z.infer<typeof invoiceDiscountSchema>;

export const invoiceCreateSchema = z.object({
  session_id: z.string().optional(),
  customer_id: z.string().optional(),
  user_id: z.string().optional(),
  status: z
    .enum(['completed', 'voided', 'draft', 'refunded', 'pending'])
    .default('completed'),
  items: z.array(invoiceLineItemSchema).min(1, 'At least one item is required'),
  discount: invoiceDiscountSchema.optional(),
  tax: z.number().min(0).max(100).optional(),
  rounding: z.number().optional(),
  payment_type: z.string().optional(),
  payment_ref: z.string().optional(),
  change: z.number().optional(),
  tendered: z.number().optional(),
  credits_used: z.number().min(0).optional(),
  points_used: z.number().min(0).optional(),
  note: z.string().optional(),
  receipt_format: z.string().optional(),
  metadata: z.string().optional(),
});

export type InvoiceCreateInput = z.infer<typeof invoiceCreateSchema>;

// ─── Session ───────────────────────────────────────────────────────────────

export const sessionToggleSchema = z.object({
  opening_cash: z.number().min(0).optional(),
  closing_cash: z.number().min(0).optional(),
});

export type SessionToggleInput = z.infer<typeof sessionToggleSchema>;

// ─── Cashflow ──────────────────────────────────────────────────────────────

export const cashflowSchema = z.object({
  session_id: z.string().optional(),
  title: z.string().min(1, 'Title is required'),
  type: z.string().optional(),
  flow: z.enum(['in', 'out']),
  value: z.number().positive('Value must be positive'),
  description: z.string().optional(),
  doc_r2_key: z.string().optional(),
  user_id: z.string().optional(),
});

export type CashflowInput = z.infer<typeof cashflowSchema>;

// ─── Expense ───────────────────────────────────────────────────────────────

export const expenseSchema = z.object({
  session_id: z.string().optional(),
  title: z.string().min(1, 'Title is required'),
  amount: z.number().positive('Amount must be positive'),
  category: z.string().optional(),
  description: z.string().optional(),
  user_id: z.string().optional(),
  metadata: z.string().optional(),
});

export type ExpenseInput = z.infer<typeof expenseSchema>;

// ─── User ──────────────────────────────────────────────────────────────────

export const userSchema = z.object({
  id: z.string().optional(),
  account_id: z.string().optional(),
  shop_id: z.string().optional(),
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(6, 'Password must be at least 6 characters').optional(),
  admin: z.number().int().min(0).max(2).default(0),
  lang: z.string().optional(),
  phone: z.string().optional(),
  ip_address: z.string().optional(),
  metadata: z.string().optional(),
});

export type UserInput = z.infer<typeof userSchema>;

// ─── Supplier ──────────────────────────────────────────────────────────────

export const supplierSchema = z.object({
  id: z.string().optional(),
  account_id: z.string().optional(),
  name: z.string().min(1, 'Name is required'),
  contact: z.string().optional(),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  address: z.string().optional(),
  metadata: z.string().optional(),
});

export type SupplierInput = z.infer<typeof supplierSchema>;

// ─── Attendance ────────────────────────────────────────────────────────────

export const attendanceSchema = z.object({
  user_id: z.string().min(1),
  clock_in: z.string().optional(),
  clock_out: z.string().optional(),
  clock_in_location: z.string().optional(),
  clock_out_location: z.string().optional(),
  note: z.string().optional(),
  metadata: z.string().optional(),
});

export type AttendanceInput = z.infer<typeof attendanceSchema>;

// ─── Filing ────────────────────────────────────────────────────────────────

export const filingSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  category: z.string().optional(),
  description: z.string().optional(),
  file_name: z.string().optional(),
  mime_type: z.string().optional(),
  r2_key: z.string().optional(),
  url: z.string().url('Invalid URL').optional(),
  size: z.number().int().min(0).optional(),
  tags: z.string().optional(),
  user_id: z.string().optional(),
  metadata: z.string().optional(),
});

export type FilingInput = z.infer<typeof filingSchema>;

// ─── Pagination ────────────────────────────────────────────────────────────

export const paginationSchema = z.object({
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(500).default(50),
  search: z.string().optional(),
  sort: z.string().optional(),
  order: z.enum(['asc', 'desc']).default('desc'),
});

export type PaginationInput = z.infer<typeof paginationSchema>;
